import { useState, useEffect } from "react";
import { useAuth } from "@/context/auth";
import { trackEvent } from "@/lib/analytics";
import { effectiveSubscriptionEnd } from "@/lib/subscription-time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Loader2, BarChart3, AlertTriangle, Crown, Sparkles, Tag, X } from "lucide-react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useGetSubscriptionPricing, getGetSubscriptionPricingQueryKey, useInitializeSubscription } from "@workspace/api-client-react";

const PLANS = [
  {
    id: "basic",
    name: "Basic",
    price: 7500,
    description: "Perfect for small shops",
    features: [
      "1,500 product limit",
      "Sales & Inventory",
      "P&L Reports",
      "Basic Reports",
      "3 Staff Accounts",
      "1,500-product CSV import",
      "100 Bulk SMS credits/month",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 12000,
    description: "For growing businesses",
    features: [
      "2,500 product limit",
      "Full POS Features",
      "P&L Analytics",
      "7 Staff Accounts",
      "Multi-Branch Management",
      "2,500-product CSV import",
      "200 Bulk SMS credits/month",
    ],
    popular: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 28000,
    description: "For large operations",
    features: [
      "Unlimited products",
      "Unlimited Staff",
      "Unlimited CSV/Excel Import",
      "Attendance Tracking",
      "Checklist Management",
      "PDF/Excel Export",
      "Advanced Analytics",
      "Email Notifications",
      "Multi-Branch Management",
      "Priority Support",
      "500 Bulk SMS credits/month",
    ],
    enterprise: true,
  },
];

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function Subscribe() {
  const { store, refetch } = useAuth();
  const [, setLocation] = useLocation();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "quarterly" | "annual">("monthly");
  const qc = useQueryClient();
  const pricing = useGetSubscriptionPricing();
  const initialize = useInitializeSubscription();
  const quoteFor = (planId: string) => pricing.data?.find(q => q.planId === planId && q.billingCycle === billingCycle);
  const [upgraded, setUpgraded] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [promoData, setPromoData] = useState<{
    durationMonths: number;
    spotsRedeemed: number;
    maxUses: number;
    usesLeft: number;
    allowedPlans: string[];
  } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get("reference");
    if (reference) verifyPayment(reference);
  }, []);

  const verifyPayment = async (reference: string) => {
    setLoading("verifying");
    try {
      const res = await fetch(`${BASE}/api/subscriptions/verify/${reference}`, { credentials: "include" });
      const data = await res.json();
      if (res.ok) {
        await refetch();
        trackEvent("subscription_completed", {
          plan: data.plan ?? "unknown",
          upgraded: Boolean(data.upgraded),
          payment_method: "paystack",
        });
        if (data.upgraded) {
          setUpgraded(data.plan);
          setTimeout(() => setLocation("/"), 3500);
        } else {
          setLocation("/");
        }
      } else {
        setError(data.error || "Payment verification failed");
      }
    } catch {
      setError("Verification failed. Please contact support.");
    } finally {
      setLoading(null);
    }
  };

  const applyPromo = async (planId?: string) => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoValidating(true); setPromoError("");
    try {
      const res = await fetch(`${BASE}/api/subscriptions/validate-promo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code, planId: planId || "basic" }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPromoError(data.error || "Could not apply promo code");
        return;
      }
      setPromoCode(code);
      setPromoData(data);
    } catch { setPromoError("Could not validate code. Try again."); }
    finally { setPromoValidating(false); }
  };

  const clearPromo = () => { setPromoCode(""); setPromoInput(""); setPromoData(null); setPromoError(""); };

  const handleSubscribe = async (planId: string) => {
    setLoading(planId); setError("");

    // If a promo code is applied and valid for this plan, redeem it for free
    if (promoCode && promoData?.allowedPlans.includes(planId)) {
      try {
        const res = await fetch(`${BASE}/api/subscriptions/redeem-promo`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ code: promoCode, planId }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || "Could not redeem promo code");
          setLoading(null);
          return;
        }
        await refetch();
        trackEvent("subscription_completed", {
          plan: planId,
          upgraded: Boolean(store?.plan && store.plan !== planId),
          payment_method: "promo",
        });
        setLocation("/");
        return;
      } catch { setError("Redemption failed. Please try again."); setLoading(null); return; }
    }
    try {
      // Save phone if provided and not already set
      const phoneToSave = phone.trim() || (!store?.phone ? null : undefined);
      if (phoneToSave) {
        await fetch(`${BASE}/api/auth/store`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ phone: phoneToSave }),
        });
      }
      const callbackUrl = `${window.location.origin}${BASE}/subscribe`;
      const quote = quoteFor(planId);
      if (!quote) {
        setError("Current pricing is not available. Reload pricing and try again.");
        pricing.refetch();
        return;
      }
      let data;
      try {
        data = await initialize.mutateAsync({ data: {
          planId: planId as "basic" | "pro" | "enterprise",
          billingCycle,
          callbackUrl,
          expectedAmountKobo: quote.amountKobo,
          discountId: quote.discountId,
        } });
      } catch (e) {
        const x = e as { status?: number; data?: { error?: string }; message?: string };
        if (x?.status === 400 || x?.status === 409) {
          await qc.invalidateQueries({ queryKey: getGetSubscriptionPricingQueryKey() });
          setError(x.data?.error || "The price changed or the offer is no longer available. Review the updated price below and try again.");
        } else {
          setError(x?.data?.error || "Failed to initialize payment");
        }
        return;
      }
      trackEvent("subscription_started", {
        plan: planId,
        billing_cycle: billingCycle,
        current_plan: store?.plan ?? "none",
      });
      window.location.href = data.authorizationUrl;
    } catch {
      setError("Payment initialization failed. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const isOnTrial = store?.subscriptionStatus === "trial";
  const trialEndsAt = isOnTrial && store?.trialEndsAt ? effectiveSubscriptionEnd(store.trialEndsAt) : null;
  const trialExpired = isOnTrial && trialEndsAt && trialEndsAt < new Date();
  const trialDays = isOnTrial && trialEndsAt && !trialExpired
    ? Math.ceil((trialEndsAt.getTime() - Date.now()) / 86400000)
    : 0;

  const quarterlyPrice = (monthly: number) => monthly * 3;
  const annualPrice = (monthly: number) => monthly * 10;
  const annualSaving = (monthly: number) => monthly * 2;
  if (loading === "verifying") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4 py-16">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-400" />
          <p className="text-slate-300">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (upgraded) {
    const upgradedPlanName = PLANS.find(p => p.id === upgraded)?.name ?? upgraded;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col items-center justify-center p-6">
        <div className="flex flex-col items-center gap-4 py-16 text-center max-w-md">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">Subscription activated</h2>
          <p className="text-slate-300 text-lg">
            Your existing upgraded plan is <strong className="text-amber-300">{upgradedPlanName}</strong>.
          </p>
          <p className="text-slate-400 text-sm">Redirecting you to your dashboard…</p>
          <Loader2 className="w-6 h-6 animate-spin text-indigo-400 mt-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-5xl">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={`${import.meta.env.BASE_URL}logo.png`} alt="RetailPulse" className="w-10 h-10 rounded-xl object-cover" />
          <span className="text-2xl font-bold text-white">RetailPulse</span>
        </div>

        <div className="text-center mb-6">
          <a href={`${BASE}/data-requests`} className="text-sm text-slate-200 underline">Request your data export or deletion</a>
          {trialExpired ? (
            <>
              <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full text-sm mb-4">
                <AlertTriangle className="w-4 h-4" />
                Your 10-day free trial has ended
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Choose a plan to continue</h1>
              <p className="text-slate-400">Your data is safe — subscribe to regain full access.</p>
            </>
          ) : trialDays > 0 ? (
            <>
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full text-sm mb-4">
                <CheckCircle className="w-4 h-4" />
                {trialDays} day{trialDays !== 1 ? "s" : ""} left in your free trial
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Upgrade your plan</h1>
              <p className="text-slate-400">Unlock more features and scale your business.</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-white mb-2">Choose your plan</h1>
              <p className="text-slate-400">All plans include a 10-day free trial.</p>
            </>
          )}
        </div>

        {/* Phone number — collect if not already on file */}
        {!store?.phone && (
          <div className="max-w-sm mx-auto mb-6 bg-white/5 border border-white/10 rounded-xl p-4">
            <label className="text-sm font-medium text-white block mb-1">📱 Your Phone Number</label>
            <Input
              type="tel"
              placeholder="+234 800 000 0000"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
            />
            <p className="text-xs text-slate-400 mt-1.5">We'll save this to your store profile so we can reach you if needed.</p>
          </div>
        )}

        {/* Promo code */}
        <div className="max-w-sm mx-auto mb-6">
          {promoCode && promoData ? (
            <div className="flex items-center gap-3 bg-emerald-500/15 border border-emerald-500/30 rounded-xl px-4 py-3">
              <Tag className="w-4 h-4 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-emerald-300 font-semibold text-sm">{promoCode} applied!</p>
                <p className="text-emerald-200/70 text-xs">
                  {promoData.durationMonths} months free on {promoData.allowedPlans.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" & ")} plans · {promoData.spotsRedeemed} of {promoData.maxUses} spots have been redeemed · {promoData.usesLeft} remaining
                </p>
              </div>
              <button onClick={clearPromo} className="text-emerald-400/60 hover:text-emerald-300 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter promo code"
                  value={promoInput}
                  onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoError(""); }}
                  onKeyDown={e => e.key === "Enter" && applyPromo()}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 uppercase"
                />
                <Button
                  variant="outline"
                  onClick={() => applyPromo()}
                  disabled={promoValidating || !promoInput.trim()}
                  className="shrink-0 border-white/20 text-white hover:bg-white/10"
                >
                  {promoValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
              {promoError && <p className="text-red-300 text-xs px-1">{promoError}</p>}
            </div>
          )}
        </div>

        {/* Billing cycle toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex items-center bg-slate-800 rounded-xl p-1 gap-1">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                billingCycle === "monthly"
                  ? "bg-white text-slate-900 shadow"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("quarterly")}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === "quarterly"
                  ? "bg-white text-slate-900 shadow"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Quarterly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === "annual"
                  ? "bg-white text-slate-900 shadow"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Annual
              <span className="bg-emerald-700 text-white text-xs px-1.5 py-0.5 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {pricing.isError && (
          <div className="max-w-lg mx-auto mb-6 bg-amber-500/20 text-amber-200 px-4 py-3 rounded-lg text-sm text-center">
            Could not load current prices. <button className="underline" onClick={() => pricing.refetch()}>Retry</button>
          </div>
        )}
        {error && (
          <div className="max-w-lg mx-auto mb-6 bg-destructive/20 text-red-300 px-4 py-3 rounded-lg text-sm text-center">{error}</div>
        )}

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 mb-8">
          {PLANS.map(plan => {
            const q = quoteFor(plan.id);
            const promoOn = !!promoCode && !!promoData?.allowedPlans.includes(plan.id);
            const offer = q && q.discountId !== null && !promoOn ? q : null;
            const regularPrice =
              billingCycle === "annual" ? annualPrice(plan.price) :
              billingCycle === "quarterly" ? quarterlyPrice(plan.price) :
              plan.price;
            const displayPrice = q ? (promoOn ? q.regularAmountKobo : q.amountKobo) / 100 : regularPrice;
            const cycleWord = billingCycle === "annual" ? "year" : billingCycle === "quarterly" ? "quarter" : "month";
            const saving = billingCycle === "annual" ? annualSaving(plan.price) : 0;
            return (
              <Card
                key={plan.id}
                className={`relative ${
                  plan.popular
                    ? "border-2 border-primary shadow-lg shadow-primary/20"
                    : plan.enterprise
                    ? "border-2 border-amber-400 shadow-lg shadow-amber-400/20"
                    : ""
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-white px-3">Most Popular</Badge>
                  </div>
                )}
                {plan.enterprise && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-700 text-white px-3 gap-1"><Crown className="w-3 h-3" /> Enterprise</Badge>
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-2">
                    <div className="flex items-end gap-1.5">
                      <span className="text-3xl font-bold">₦{displayPrice.toLocaleString("en-NG")}</span>
                      <span className="text-muted-foreground text-sm pb-0.5">
                        {billingCycle === "annual" ? "/year" : billingCycle === "quarterly" ? "/quarter" : "/month"}
                      </span>
                    </div>
                    {offer && (
                      <div className="mt-1 space-y-1" data-testid={`offer-${plan.id}`}>
                        <p className="text-sm text-muted-foreground">
                          Regular <span className="line-through">₦{(offer.regularAmountKobo / 100).toLocaleString("en-NG")}</span>/{cycleWord} · {offer.discountPercent}% off
                        </p>
                        <p className="text-xs text-emerald-700 font-medium">
                          {billingCycle === "monthly"
                            ? `Paystack charges ₦${(offer.amountKobo / 100).toLocaleString("en-NG")} per month for at most ${offer.maxPayments} payment${offer.maxPayments !== 1 ? "s" : ""}, then stops. To keep your plan, pay through Paystack again at the standard price.`
                            : `Paystack charges ₦${(offer.amountKobo / 100).toLocaleString("en-NG")} per ${cycleWord} for at most ${offer.maxPayments} payment${offer.maxPayments !== 1 ? "s" : ""}, then stops. To renew afterward, pay through Paystack again at the standard price.`}
                          {offer.reason ? ` (${offer.reason})` : ""}
                          {offer.maxPayments === 1 ? " This is a one-off payment; automatic billing is not enabled." : " The first payment counts toward this limit."}
                        </p>
                      </div>
                    )}
                    {!offer && q && q.discountId !== null && promoOn && (
                      <p className="text-xs text-slate-500 mt-1">Your promo code takes precedence over the business offer.</p>
                    )}
                    {billingCycle === "annual" && !offer && (
                      <p className="text-emerald-600 text-xs mt-1 font-medium">
                        Save ₦{saving.toLocaleString("en-NG")}/year · 2 months free
                      </p>
                    )}
                    {billingCycle === "quarterly" && (
                      <p className="text-slate-400 text-xs mt-1">
                        ₦{plan.price.toLocaleString("en-NG")}/mo · billed every 3 months
                      </p>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 ${plan.enterprise ? "text-amber-500" : "text-emerald-500"}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {(() => {
                    const promoValid = promoCode && promoData?.allowedPlans.includes(plan.id);
                    return (
                      <Button
                        className={`w-full ${plan.enterprise ? "bg-amber-700 hover:bg-amber-800 text-white" : promoValid ? "bg-emerald-700 hover:bg-emerald-800 text-white border-0" : ""}`}
                        variant={plan.popular && !promoValid ? "default" : "outline"}
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={!!loading || (!promoValid && !q)}
                      >
                        {loading === plan.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {promoValid
                          ? <><Tag className="w-4 h-4 mr-1.5" /> Get {promoData!.durationMonths} Months Free</>
                          : plan.enterprise
                          ? <><Crown className="w-4 h-4 mr-1" /> Subscribe</>
                          : billingCycle === "annual"
                          ? "Subscribe Annually"
                          : billingCycle === "quarterly"
                          ? "Subscribe Quarterly"
                          : "Subscribe with Paystack"}
                      </Button>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center text-slate-400 text-sm">
          Secure payments powered by Paystack · {billingCycle === "annual" ? "Billed once per year" : billingCycle === "quarterly" ? "Billed every 3 months" : "Cancel anytime"}
        </div>
      </div>
    </div>
  );
}
